import Select from '@/components/form/select';
import Text from '@/components/text';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import {
  MetadataControlConfig,
  MetadataControlType,
} from '@/lib/models/policy-model/metadata-controls';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import { Box, Stack, Theme } from '@mui/material';
import { FC, ReactNode, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { getFormattedUiOptions } from '../select/helpers';
import { ControlActionValue } from '@/lib/models/policy-model/controls';
import { UiOption } from '@/lib/helpers';
import InputControl from '../input-control';

interface Props {
  formHandle: FormHandle<PolicyModel>;
  readonly: boolean;
  label: string;
  description: ReactNode;
  controlType: MetadataControlType;
  options: UiOption[];
  withNotSet?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  serviceControlItem: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      marginBottom: 8,
      paddingBottom: 8,
    },
  },
}));

const ServiceControlItem: FC<Props> = ({
  formHandle,
  readonly,
  label,
  description,
  controlType,
  options,
  withNotSet = true,
}) => {
  const styles = useStyles();
  const { values, setFieldValue } = formHandle;

  const [entity, entityIndex] = useMemo(() => {
    const entityIndex =
      values.metadataControls?.entities?.findIndex(({ type }) => type === controlType) || -1;
    return entityIndex > -1
      ? [values.metadataControls?.entities?.[entityIndex], entityIndex]
      : [undefined, -1];
  }, [controlType, values.metadataControls]);

  const handleChange = (settingType: MetadataControlType, entityIndex: number, action: string) => {
    if (!values.metadataControls) {
      values.metadataControls = new MetadataControlConfig({});
    }

    const base = values.metadataControls?.entities ?? [];
    const updated = [...base];

    if (entityIndex > -1) {
      if (action) {
        updated[entityIndex] = {
          ...updated[entityIndex],
          action: action as ControlActionValue,
        };
      } else {
        updated.splice(entityIndex, 1);
      }
    } else if (action) {
      updated.push({ type: settingType, action: action as ControlActionValue });
    }

    setFieldValue('metadataControls.entities', updated);
  };

  const niceOpts = useMemo(() => getFormattedUiOptions(options), [options]);
  const isDataPrivacyControl = controlType === 'enforce-data-privacy';

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      className={styles.serviceControlItem}
    >
      <Box pr={4}>
        <Text>{label}</Text>
        <Box mt={0.5}>
          <Text size="small" color="grey">
            {description}
          </Text>
        </Box>
      </Box>
      <Box minWidth={250}>
        {isDataPrivacyControl ? (
          <InputControl
            name="metadataControls.forceDataPrivacy"
            formHandle={formHandle}
            type="boolean-select"
            options={niceOpts}
            readonly={readonly}
            size="small"
            withNotSet={withNotSet}
          />
        ) : (
          <Select
            name={`metadataControls.${controlType}`}
            options={niceOpts}
            readonly={readonly}
            value={entity?.action}
            onChange={(value: string) => handleChange(controlType, entityIndex, value)}
            size="small"
            withNotSet={withNotSet}
          />
        )}
      </Box>
    </Stack>
  );
};

export default ServiceControlItem;
